/*!
 * curves.js — the curves a binding or a level fade maps along
 * © 2026 Rob Canning — GPLv3
 *
 * One table for the parser (which names follow() takes), applyCurve
 * (paramBinding.js) and its inverse (cueParamBinding.js). A leaf: the
 * parser cannot import paramBinding.js, which imports the parser.
 */

/** name -> [forward, inverse], both on 0..1 */
const CURVES = {
  lin: [t => t, t => t],
  // slower at low values, faster at high
  exp2: [t => t * t, t => Math.sqrt(t)],
  exp3: [t => t * t * t, t => Math.pow(t, 1 / 3)],
  exp4: [t => t * t * t * t, t => Math.pow(t, 1 / 4)],
  // faster at low values, slower at high: log1p(t * 9) / log(10)
  log: [t => (t === 0 ? 0 : Math.log1p(t * 9) / Math.log(10)), t => (t === 0 ? 0 : (Math.pow(10, t) - 1) / 9)],
  // the opposite of exp2
  sqrt: [t => Math.sqrt(t), t => t * t],
};
const ALIASES = { linear: "lin", logarithmic: "log", exp: "exp2" };

/** The names follow() takes: the table's and the aliases. */
export const CURVE_NAMES = new Set([...Object.keys(CURVES), ...Object.keys(ALIASES)]);

/** A curve's two functions; an unknown name is lin. */
const curveOf = (name) => {
  const n = String(name ?? "lin").toLowerCase();
  return CURVES[ALIASES[n] ?? n] ?? CURVES.lin;
};
const clamp01 = t => Math.max(0, Math.min(1, t));

/** t (0..1) along the curve. */
export const applyCurve = (t, curve = "lin") => curveOf(curve)[0](clamp01(t));

/** The t that applyCurve maps to `out`. */
export const invertCurve = (out, curve = "lin") => curveOf(curve)[1](clamp01(out));
